@file:OptIn(ExperimentalTime::class)

package nl.volleyplanner.extensions

import kotlinx.datetime.*
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

val DEFAULT_TIMEZONE = TimeZone.of("Europe/Amsterdam")

@ConsistentCopyVisibility
data class MonthDay private constructor(
    val month: Int,
    val dayOfMonth: Int
) {
    companion object {
        fun of(month: Int, dayOfMonth: Int): MonthDay {
            require(month in 1..12) { "Month must be between 1 and 12" }
            require(dayOfMonth in 1..31) { "Day must be between 1 and 31" }
            return MonthDay(month, dayOfMonth)
        }

        fun from(localDate: LocalDate) = MonthDay(localDate.month.number, localDate.day)
        fun from(localDateTime: LocalDateTime) = MonthDay(localDateTime.month.number, localDateTime.day)
    }

    fun atYear(year: Int) = LocalDate(year, month, dayOfMonth)

    fun isBefore(other: MonthDay) = month < other.month || (month == other.month && dayOfMonth < other.dayOfMonth)
    fun isAfter(other: MonthDay) = month > other.month || (month == other.month && dayOfMonth > other.dayOfMonth)
}

fun LocalDateTime.Companion.now() = Clock.System.now().toLocalDateTime(DEFAULT_TIMEZONE)
fun LocalDate.Companion.today() = Clock.System.todayIn(DEFAULT_TIMEZONE)

fun LocalDateTime.durationUntil(other: LocalDateTime, timeZone: TimeZone = DEFAULT_TIMEZONE): DateTimePeriod {
    val thisInstant = this.toInstant(timeZone)
    val otherInstant = other.toInstant(timeZone)
    return thisInstant.periodUntil(otherInstant, timeZone)
}

fun DateTimePeriod.compareTo(other: DateTimePeriod): Int {
    return when {
        years != other.years -> years.compareTo(other.years)
        months != other.months -> months.compareTo(other.months)
        days != other.days -> days.compareTo(other.days)
        hours != other.hours -> hours.compareTo(other.hours)
        minutes != other.minutes -> minutes.compareTo(other.minutes)
        seconds != other.seconds -> seconds.compareTo(other.seconds)
        nanoseconds != other.nanoseconds -> nanoseconds.compareTo(other.nanoseconds)
        else -> 0
    }
}


/**
 * Returns the age for a date and given cut-off date
 *
 * Useful for determining someones age from a birth date for a sports/school season
 * where different rules or categories can apply for different age groups.
 *
 * Examples:
 * {@code LocalDate.of(2008, 9, 1).getAgeGroup(LocalDate.of(2020, 9, 1)} returns 12
 * {@code LocalDate.of(2008, 9, 2).getAgeGroup(LocalDate.of(2020, 9, 1)} returns 11
 *
 * @return The 'age' of the date at the provided cut-off date
 */
fun LocalDate.getAge(cutoffDate: LocalDate) : Int {
    return this.periodUntil(cutoffDate).years
}
